import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { camelize, decamelize } from 'humps'
import { Table, Column, Menu, Dropdown, Icon, Spin } from './common/Ant'
import { Anchor, ActionAnchor } from './common/Anchor'
import SearchFilter from './common/SearchFilter'
import Select, { Option } from './common/Select'
import { hasPermission } from '../utils/hasPermission'
import { createQueryString, parseQueryParams } from '../utils/queryParams'
import {
  DEFAULT_PAGE,
  DEFAULT_PER_PAGE,
  USER_PERMISSIONS,
  VIEW_ALL,
  FLOW_MONITOR_STATUS,
  WATER_VALVE_STATUS,
  WATER_METER_TYPES,
} from '../constants'
import { toTitleCase } from '../utils/textFormatters'
import { configurePagination } from '../utils/helpers'
import LastSnapshot from './LastSnapshot'

const parseSortFromQuery = sort => {
  if (!sort) {
    return {}
  }

  const desc = sort.startsWith('-')
  const field = camelize(desc ? sort.slice(1) : sort)

  return {
    field,
    order: desc ? 'descend' : 'ascend',
    columnKey: field,
  }
}

const buildSortQueryParam = sortedInfo => {
  if (!sortedInfo || !sortedInfo.field || !sortedInfo.order) {
    return undefined
  }

  const prefix = sortedInfo.order === 'descend' ? '-' : ''
  const field = decamelize(sortedInfo.field)

  return `${prefix}${field}`
}

const normalizeSorter = sorter => {
  if (!sorter) {
    return {}
  }

  const active = Array.isArray(sorter)
    ? sorter.find(column => column.order) || {}
    : sorter
  const field = active.field || active.columnKey

  if (!field || !active.order) {
    return {}
  }

  return {
    field,
    order: active.order,
    columnKey: field,
  }
}

class AllFlowMonitors extends Component {
  constructor(props) {
    super(props)

    this._isMounted = false

    this.state = {
      sortedInfo: {},
      searchText: '',
      page: DEFAULT_PAGE,
      perPage: DEFAULT_PER_PAGE,
      status: undefined,
      siteSlug: undefined,
      assetLocationId: undefined,
      meterType: undefined,
    }
  }

  static propTypes = {
    isSnapshotLoading: PropTypes.bool.isRequired,
    snapshotData: PropTypes.object.isRequired,
    getLocationSnapshot: PropTypes.func.isRequired,
    getAllFlowMonitors: PropTypes.func.isRequired,
    flowMonitors: PropTypes.arrayOf(PropTypes.object).isRequired,
    windowWidth: PropTypes.number.isRequired,
    isFlowMonitorLoading: PropTypes.bool.isRequired,
    deleteFlowMonitor: PropTypes.func.isRequired,
    getAllSites: PropTypes.func.isRequired,
    getAllAssetLocations: PropTypes.func,
    sites: PropTypes.arrayOf(PropTypes.object),
    assetLocations: PropTypes.arrayOf(PropTypes.object),
    updateQueryParams: PropTypes.func,
    meta: PropTypes.object,
  }

  safeSetState = (state, callback) => {
    if (this._isMounted) {
      this.setState(state, callback)
    }
  }

  buildFlowMonitorsQuery = (overrides = {}) => {
    const {
      page,
      perPage,
      status,
      siteSlug,
      assetLocationId,
      meterType,
      searchText,
      sortedInfo,
    } = { ...this.state, ...overrides }
    const sort = buildSortQueryParam(sortedInfo)

    return {
      page,
      perPage,
      status,
      siteSlug,
      assetLocationId,
      meterType,
      ...(searchText ? { filter: searchText } : {}),
      ...(sort ? { sort } : {}),
    }
  }

  componentDidMount() {
    this._isMounted = true

    const { getAllFlowMonitors } = this.props

    const {
      page,
      perPage,
      status,
      siteSlug,
      assetLocationId,
      meterType,
      filter,
      sort,
    } = parseQueryParams(global.location)

    const sortedInfo = parseSortFromQuery(sort)
    const initialQuery = {
      page: page ? parseInt(page, 10) : DEFAULT_PAGE,
      perPage: perPage ? parseInt(perPage, 10) : DEFAULT_PER_PAGE,
      status,
      siteSlug,
      assetLocationId,
      meterType,
      searchText: filter || '',
      sortedInfo,
    }

    this.safeSetState(initialQuery)

    getAllFlowMonitors(
      createQueryString(this.buildFlowMonitorsQuery(initialQuery))
    )
  }

  componentWillUnmount() {
    this._isMounted = false
  }

  handleChange = (pagination, filters, sorter) => {
    const { getAllFlowMonitors, updateQueryParams } = this.props
    const { page, sortedInfo: prevSortedInfo } = this.state
    const normalizedSorter = normalizeSorter(sorter)
    const pageChanged = page !== pagination.current
    const sortChanged =
      buildSortQueryParam(prevSortedInfo) !==
      buildSortQueryParam(normalizedSorter)
    const nextPage =
      sortChanged && !pageChanged ? DEFAULT_PAGE : pagination.current
    const nextSortedInfo = normalizedSorter.order
      ? normalizedSorter
      : sortChanged
      ? {}
      : prevSortedInfo

    if (pageChanged || sortChanged) {
      const qs = createQueryString(
        this.buildFlowMonitorsQuery({
          page: nextPage,
          sortedInfo: nextSortedInfo,
        })
      )

      getAllFlowMonitors(qs)
      updateQueryParams && updateQueryParams({ search: qs })
    }

    this.safeSetState({
      page: nextPage,
      sortedInfo: nextSortedInfo,
    })
  }

  handleFilterChange = keyName => value => {
    const { getAllFlowMonitors, updateQueryParams } = this.props

    const newValue =
      value === undefined || (value.length && value.indexOf('all')) > -1
        ? undefined
        : value

    const resetPage = keyName !== 'page' && keyName !== 'perPage'
    const overrides = {
      [keyName]: newValue,
      ...(resetPage ? { page: DEFAULT_PAGE } : {}),
    }

    this.safeSetState(overrides)

    const qs = createQueryString(this.buildFlowMonitorsQuery(overrides))
    getAllFlowMonitors(qs)
    updateQueryParams && updateQueryParams({ search: qs })
  }

  setSearchText = searchText => this.safeSetState({ searchText })

  columnSorter = field => {
    const { sortedInfo } = this.state
    const sortOrder = sortedInfo.field === field ? sortedInfo.order : false

    return {
      key: field,
      sorter: true,
      sortOrder,
    }
  }

  render() {
    const {
      isSnapshotLoading,
      snapshotData,
      getLocationSnapshot,
      deleteFlowMonitor,
      windowWidth,
      isFlowMonitorLoading,
      meta,
      sites,
      assetLocations,
      getAllFlowMonitors,
      flowMonitors,
    } = this.props
    const {
      searchText,
      perPage,
      status,
      siteSlug,
      assetLocationId,
      meterType,
    } = this.state

    const isFixedColumn = windowWidth < 750

    const expandedRowRender = record => (
      <div className="mt2">
        {record.locationId ? (
          <LastSnapshot
            isFlowMonitor
            siteSlug={record.siteSlug}
            floorId={record.floorId}
            locationId={record.locationId}
            isSnapshotLoading={isSnapshotLoading}
            snapshotData={snapshotData}
            getLocationSnapshot={getLocationSnapshot}
          />
        ) : (
          <p className="tc">No location available.</p>
        )}
      </div>
    )

    const query = this.buildFlowMonitorsQuery()

    return (
      <section className="mb5 AllFlowMonitors">
        {hasPermission(USER_PERMISSIONS.ADD_FLOW_MONITOR) && (
          <Anchor
            to="/inventory/flow-monitors/create"
            button
            className="mr3 mb3 dib"
          >
            Create New
          </Anchor>
        )}
        <div className="flex-l items-center-l">
          <Select
            className="w-20-l mr3-l"
            placeholder="Site"
            mode="multiple"
            input={{
              value: siteSlug,
              onChange: this.handleFilterChange('siteSlug'),
            }}
            filterable
          >
            {[{ slug: 'all', name: VIEW_ALL }, ...(sites || [])].map(x => (
              <Option value={x.slug} key={x.slug}>
                {x.name}
              </Option>
            ))}
          </Select>
          <Select
            className="w-20-l mr3-l"
            placeholder="Asset Location"
            mode="multiple"
            input={{
              value: assetLocationId,
              onChange: this.handleFilterChange('assetLocationId'),
            }}
            filterable
          >
            {[{ id: 'all', pillarId: VIEW_ALL }, ...(assetLocations || [])].map(
              x => (
                <Option value={x.id} key={x.id}>
                  {x !== 'all' || x !== null ? x.pillarId : VIEW_ALL}
                </Option>
              )
            )}
          </Select>
          <Select
            className="w-20-l mr3-l"
            placeholder="Status"
            mode="multiple"
            input={{
              value: status,
              onChange: this.handleFilterChange('status'),
            }}
            dropdownMatchSelectWidth={false}
            filterable
          >
            {['all', ...Object.keys(FLOW_MONITOR_STATUS)].map(x => (
              <Option value={x} key={x}>
                {toTitleCase(FLOW_MONITOR_STATUS[x]) || VIEW_ALL}
              </Option>
            ))}
          </Select>
          <Select
            className="w-20-l mr3-l"
            placeholder="Meter Type"
            mode="multiple"
            input={{
              value: meterType,
              onChange: this.handleFilterChange('meterType'),
            }}
            dropdownMatchSelectWidth={false}
            filterable
          >
            {['all', ...Object.keys(WATER_METER_TYPES)].map(x => (
              <Option value={x} key={x}>
                {WATER_METER_TYPES[x] || VIEW_ALL}
              </Option>
            ))}
          </Select>
        </div>
        <SearchFilter
          searchText={searchText}
          setSearchText={this.setSearchText}
          getItems={getAllFlowMonitors}
          query={query}
          id="inventory-flow-monitors-search"
        />
        {isFlowMonitorLoading ? (
          <Spin size="large" className="w-100 center mt5" />
        ) : (
          <Table
            className="nested-table"
            dataSource={flowMonitors}
            onChange={this.handleChange}
            expandedRowRender={expandedRowRender}
            rowKey="id"
            scroll={{ x: 650 }}
            bordered
            pagination={configurePagination({
              perPage,
              onShowSizeChange: (page, perPage) =>
                this.handleFilterChange('perPage')(perPage),
              ...meta,
            })}
            tableLayout="auto"
            rowClassName={record =>
              record.valveId
                ? record.valveStatus === WATER_VALVE_STATUS.OPEN
                  ? 'bg-light-green'
                  : 'bg-light-red'
                : ''
            }
          >
            <Column
              fixed="left"
              title="Pillar ID"
              dataIndex="pillarId"
              width={150}
              {...this.columnSorter('pillarId')}
              render={(text, { id }) =>
                hasPermission(USER_PERMISSIONS.EDIT_FLOW_MONITOR) ? (
                  <Anchor to={`/inventory/flow-monitors/${id}`}>{text}</Anchor>
                ) : (
                  text
                )
              }
            />
            <Column
              fixed="left"
              title="Smart Pod"
              dataIndex="podPillarId"
              width={150}
              {...this.columnSorter('podPillarId')}
              render={text =>
                text ? (
                  <Anchor to={`/inventory/pods/${text}`}>{text}</Anchor>
                ) : (
                  '--'
                )
              }
            />
            <Column
              title="Site"
              dataIndex="siteName"
              width={200}
              {...this.columnSorter('siteName')}
              render={(text, { siteSlug }) =>
                siteSlug ? (
                  <Anchor to={`/sites/${siteSlug}`}>{text}</Anchor>
                ) : (
                  text
                )
              }
            />
            <Column
              title="Location"
              dataIndex="locationName"
              width={150}
              {...this.columnSorter('locationName')}
              render={(text, r) =>
                hasPermission(USER_PERMISSIONS.VIEW_FLOW_MONITOR) &&
                r.siteSlug &&
                r.floorId &&
                r.locationId ? (
                  <Anchor
                    to={`/sites/${r.siteSlug}/floors/${r.floorId}/locations/${r.locationId}/flow-monitor-chart`}
                  >
                    {text}
                  </Anchor>
                ) : (
                  text
                )
              }
            />
            <Column
              title="Status"
              dataIndex="status"
              width={150}
              {...this.columnSorter('status')}
              render={text => (text ? toTitleCase(text) : '--')}
            />
            <Column
              title="Pipe OD"
              dataIndex="pipeOd"
              width={150}
              {...this.columnSorter('pipeOd')}
              render={text => (text ? text : '--')}
            />
            <Column
              title="Pipe Max Flow"
              dataIndex="pipeMaxFlow"
              width={150}
              {...this.columnSorter('pipeMaxFlow')}
              render={text => (text ? text : '--')}
            />
            <Column
              title="Meter Type"
              dataIndex="meterType"
              width={150}
              {...this.columnSorter('meterType')}
              render={text => (text ? WATER_METER_TYPES[text] : '--')}
            />
            <Column
              title="Meter Serial Id"
              dataIndex="meterSerialId"
              width={150}
              {...this.columnSorter('meterSerialId')}
              render={text => (text ? text : '--')}
            />
            <Column
              title="Valve Serial Id"
              dataIndex="valveSerialId"
              width={150}
              {...this.columnSorter('valveSerialId')}
              render={text => (text ? text : '--')}
            />
            <Column
              title="Expected Valve Status"
              dataIndex="valveStatus"
              width={150}
              {...this.columnSorter('valveStatus')}
              render={text => (text ? toTitleCase(text) : '--')}
            />
            {hasPermission(USER_PERMISSIONS.EDIT_FLOW_MONITOR) && (
              <Column
                fixed={isFixedColumn ? 'right' : false}
                width={50}
                render={(text, record) => {
                  const menu = (
                    <Menu>
                      <Menu.Item key="edit-flow-monitor">
                        <Anchor
                          to={`/inventory/flow-monitors/${record.id}/edit`}
                        >
                          Edit
                        </Anchor>
                      </Menu.Item>
                      <Menu.Item key="delete-flow-monitor">
                        <ActionAnchor
                          onClick={() => deleteFlowMonitor(record.id)}
                        >
                          Delete
                        </ActionAnchor>
                      </Menu.Item>
                    </Menu>
                  )
                  return (
                    <Dropdown overlay={menu} trigger={['click']}>
                      <a className="ant-dropdown-link">
                        Actions <Icon type="down" />
                      </a>
                    </Dropdown>
                  )
                }}
              />
            )}
          </Table>
        )}
      </section>
    )
  }
}

export default AllFlowMonitors
